import NextHead from 'next/head'
import ReactHead from '@hashicorp/react-head'
import { IconArrowLeft16 } from '@hashicorp/flight-icons/svg-react/arrow-left-16'
import { Button } from '@hashicorp/react-mds/src/components/button'
import Recorded from 'components/_page-components/events/webinar-states/recorded'
import Live from 'components/_page-components/events/webinar-states/live'
import {
	convertTimeToLocalOffset,
	convertTimeToShortDate,
	differenceInMinutes,
} from 'components/_page-components/events/event-data-utils/datetime-utils'
import rivetQuery from '@hashicorp/platform-cms'
import markdownToHtml from '@hashicorp/platform-markdown-utils/markdown-to-html'
import { memoizedGetFormProps } from '@hashicorp/react-components/src/components/marketo-form/server/client'
import { EventDocument } from 'types/dato'
import { fetchAll } from 'lib/dato/fetch'
import { determineMarketoFormId } from 'lib/dato/utils'
import getReadableTime from 'lib/get-readable-time'
import { getRevalidateTime } from 'lib/get-revalidate-time'
import { withStandardI18nParams } from 'lib/i18n/helpers/with-standard-i18n-params'
import { parseUrl } from '@hashicorp/react-components/src/lib/parse-url'
import s from './webinar.module.css'

function RenderWebinarState({ event, marketoForm }) {
	const webinarHasStarted = hasWebinarStarted(event.startTime)
	const recorded = webinarHasStarted && event.videoUrl
	const live = !webinarHasStarted
	const pending = webinarHasStarted && !recorded

	// Aug 20 2020
	const date = convertTimeToShortDate(event.startTime)
	// 2:00 PM PST (or PDT) - this is taking place in browser and is wise to Daylight vs. Standard
	const time = convertTimeToLocalOffset(event.startTime)
	const tagLine = `${date}  |  ${time} ${
		event.duration ? `  |  ${event.duration}` : ''
	}`

	const Back = () => (
		<BackButton
			{...(live
				? { text: 'Back to all events', url: '/events' }
				: {
						text: 'Back to all recorded webinars',
						url: '/events/webinars/recorded',
				  })}
		/>
	)

	if (recorded) {
		return <Recorded tagLine={tagLine} {...event} marketoForm={marketoForm} />
	}

	if (live) {
		return (
			<>
				<Back />
				<Live
					tagLine={tagLine}
					{...event}
					pending={false}
					marketoForm={marketoForm}
				/>
			</>
		)
	}

	if (pending) {
		return (
			<>
				<Back />
				<Live tagLine={tagLine} {...event} pending marketoForm={marketoForm} />
			</>
		)
	}

	return null
}

function BackButton({ text, url }) {
	return (
		<Button
			className={s.backButton}
			type="button"
			title={text}
			url={url}
			theme={{ variant: 'tertiary-neutral' }}
			icon={
				<IconArrowLeft16
					style={{ display: 'block' }}
					width="100%"
					height="100%"
				/>
			}
			iconPosition="leading"
		/>
	)
}

function hasWebinarStarted(startTime, minutesBeforeStartTime = 15) {
	const currentTime = Date.now()
	const webinarStart = new Date(startTime).getTime()
	const comparisonTime = minutesBeforeStartTime
		? webinarStart - minutesBeforeStartTime * 60
		: webinarStart
	return currentTime > comparisonTime
}

export default function WebinarPage({ event, marketoForm }) {
	return (
		<>
			{event.hidden ? (
				// Hidden is a toggle in Dato that explicitly hides the page from indexing
				<NextHead>
					<meta name="robots" content="noindex" key="robots" />
				</NextHead>
			) : (
				<ReactHead
					title={event.title}
					pageName={event.title}
					description={event.description}
					image="https://www.datocms-assets.com/2885/1599178066-events-social-share.jpg"
					twitterCard="summary_large_image"
				>
					<meta name="twitter:title" content={event.title} />
					<meta name="twitter:description" content={event.description} />
				</ReactHead>
			)}

			<div className={`g-grid-container ${s.root}`}>
				<RenderWebinarState event={event} marketoForm={marketoForm} />
			</div>
		</>
	)
}

export async function getStaticPaths() {
	const {
		_allEventV2sMeta: { count },
	} = await rivetQuery({
		query: `
  query EventsCount {
    _allEventV2sMeta {
      count
    }
  }
`,
	})

	const allEventV2s = await fetchAll(async (offset, limit) => {
		const data = await rivetQuery({
			query: `
    query AllEvents {
      allEventV2s(skip: ${offset}, first: ${limit}) {
        slug
        endTime
        eventType {
          __typename
        }
      }
    }
  `,
		})
		return data.allEventV2s
	}, count)

	return {
		paths: withStandardI18nParams(
			allEventV2s
				.filter((a) => a.eventType[0]?.__typename === 'EventWebinarRecord')
				// only statically generate webinars that end in the future
				.filter((a) => new Date(a.endTime) > new Date())
				.map((event) => ({
					params: { slug: event.slug },
				}))
		),
		fallback: 'blocking',
	}
}

export async function getStaticProps({ params: { slug: eventSlug } }) {
	const { eventV2 } = (await rivetQuery({
		query: EventDocument,
		dependencies: [],
		variables: {
			eventSlug,
		},
	})) as $TSMoveToUrql
	// Because this page is using fallback: 'blocking', return a 404 when an eventV2 does not exist.

	if (!eventV2) {
		return {
			notFound: true,
		}
	}

	const { title, eventType, startTime, endTime, tags, product } = eventV2

	// Requests for event pages with a type of `EventExternalWebinarRecord` need to be redirected to
	// the URL specified in the record
	if (eventType[0].__typename === 'EventExternalWebinarRecord') {
		return {
			redirect: {
				destination: parseUrl(eventType[0].url).href,
			},
		}
	}

	// Return a 404 when the event type isn't an internal/external webinar.
	const isWebinar = eventType[0].__typename === 'EventWebinarRecord'
	if (!isWebinar) {
		return {
			notFound: true,
		}
	}

	const minutesDuration = endTime ? differenceInMinutes(endTime, startTime) : 0

	const duration = minutesDuration > 0 ? getReadableTime(minutesDuration) : 0
	const webinarFields = eventType[0]

	const {
		speakers,
		hidden,
		recording,
		recordingVideoUrl,
		logos,
		description,
		whatWillILearn,
		agenda,
		recordingGated,
		webinarId,
		marketoForm: marketoFormIdentifier,
	} = webinarFields

	const GATED_WEBINAR_FORM_ID = determineMarketoFormId(
		marketoFormIdentifier,
		2334
	)
	const marketoForm = await memoizedGetFormProps(GATED_WEBINAR_FORM_ID)
	const getVideoUrl = () => {
		if (recordingVideoUrl) {
			return recordingVideoUrl
		}

		if (recording) {
			return recording.url
		}

		return null
	}
	return {
		props: {
			event: {
				hidden: hidden || false,
				title,
				startTime,
				endTime,
				duration,
				recordingGated,
				tags,
				webinarId,
				product: product?.map((p) => p.slug) || [],
				partnerLogos:
					logos && logos.length
						? logos.map((logo) => {
								return {
									url: logo.logo.url,
									alt: logo.name,
								}
						  })
						: null,
				description: description ? await markdownToHtml(description) : null,
				// Use markdownToHtml on the server and stringify to pass thru as prop
				content: whatWillILearn ? await markdownToHtml(whatWillILearn) : null,
				agenda: agenda,
				videoUrl: getVideoUrl(),
				speakers: speakers?.map(formatSpeakers) || [],
			},
			marketoForm,
		},
		revalidate: getRevalidateTime(),
	}
}

const formatSpeakers = ({
	name,
	jobTitle,
	photo,
	socialProfiles,
	company,
}) => ({
	name,
	description: jobTitle,
	photo: photo?.url
		? { url: photo.url, width: photo.width, height: photo.height }
		: null,
	companyName: company?.name || '',
	social: socialProfiles,
})
